import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Bot, Building2, CreditCard, MessageSquare, type LucideIcon } from 'lucide-react';
import { Page, PageHeader } from '@ampm-7en-ai/ui/components/Page';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { IconChip } from '@ampm-7en-ai/ui/components/IconChip';
import { SegmentedControl, type SegmentedOption } from '@ampm-7en-ai/ui/components/SegmentedControl';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { Skeleton } from '@ampm-7en-ai/ui/components/Skeleton';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { ChartEmpty, ChartTooltip } from '@ampm-7en-ai/ui/components/Chart';
import { axisProps, gridProps, lineCursor, tooltipCursor } from '@ampm-7en-ai/ui/components/chartStyle';
import { money } from '@/lib/money';
import {
  useDashboard,
  type GrowthDataSections,
  type GrowthDatum,
  type RevenueDatum,
  type RevenueValue,
} from '@/hooks/useDashboard';

/**
 * The platform at a glance, rebuilt on the shared component library.
 *
 * Four headline numbers, then the three questions a founder opens this page
 * to ask: is the platform growing, are people talking to it, and is it making
 * money. Each chart owns its own period, because the answers live on
 * different timescales.
 */

/* ------------------------------------------------------------------ *
 * Reading the numbers
 * ------------------------------------------------------------------ */

/** Revenue arrives as a number, or as an object wrapping one. */
const amount = (value: RevenueValue | undefined): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = value?.parsedValue ?? Number(value?.source ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const count = (value: number) => new Intl.NumberFormat().format(Math.round(value || 0));

type Period = keyof GrowthDataSections;
type RevenueMetric = 'subscriptions' | 'add_on' | 'topups' | 'revenue';

const PERIODS: SegmentedOption<Period>[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const REVENUE_METRICS: { value: RevenueMetric; label: string }[] = [
  { value: 'subscriptions', label: 'Subscriptions' },
  { value: 'add_on', label: 'Add-ons' },
  { value: 'topups', label: 'Top-ups' },
  { value: 'revenue', label: 'Everything' },
];

/* ------------------------------------------------------------------ *
 * Pieces
 * ------------------------------------------------------------------ */

/** One headline number, with somewhere to go if you want the detail. */
function Stat({
  icon: Icon,
  label,
  value,
  href,
  loading,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  href?: string;
  loading?: boolean;
}) {
  const body = (
    <Panel
      padding="md"
      hoverable={Boolean(href)}
      className="flex h-full items-start gap-3"
    >
      <IconChip icon={Icon} tone="accent" />
      <div className="min-w-0">
        <p className="text-ui-xs text-ui-fg-muted">{label}</p>
        {loading ? (
          <Skeleton className="mt-1.5 h-7 w-20" />
        ) : (
          <p className="mt-0.5 text-ui-2xl text-ui-fg">{value}</p>
        )}
      </div>
    </Panel>
  );

  return href ? (
    <Link to={href} className="rounded-ui-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring">
      {body}
    </Link>
  ) : (
    body
  );
}

/** A chart with its own heading and period control. */
function ChartCard({
  title,
  description,
  action,
  loading,
  empty,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  loading?: boolean;
  empty?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Panel padding="md" className="flex min-w-0 flex-col">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-ui-base font-medium text-ui-fg">{title}</h2>
          {description && <p className="mt-0.5 text-ui-xs text-ui-fg-muted">{description}</p>}
        </div>
        {action}
      </header>
      {loading ? <Skeleton className="h-[260px] w-full rounded-ui-md" /> : empty ? <ChartEmpty height={260} /> : children}
    </Panel>
  );
}

/* ------------------------------------------------------------------ *
 * The page
 * ------------------------------------------------------------------ */

export default function SuperAdminDashboardV2() {
  const { data, isLoading, error } = useDashboard();

  const [period, setPeriod] = React.useState<Period>('monthly');
  const [revenueMetric, setRevenueMetric] = React.useState<RevenueMetric>('subscriptions');

  const growth: GrowthDatum[] = data?.growthData?.[period] ?? [];
  const conversations = data?.conversationData ?? [];
  const revenue: RevenueDatum[] = data?.revenueData ?? [];
  const teams = data?.businessTeamMembersData ?? [];
  const stats = data?.stats;

  // Revenue is read per metric, so the chart draws one comparable series.
  const revenueSeries = React.useMemo(
    () => revenue.map((row) => ({ month: row.month, value: amount(row[revenueMetric]) })),
    [revenue, revenueMetric]
  );

  const growthEmpty = growth.every((row) => !row.businesses && !row.users);
  const revenueEmpty = revenueSeries.every((row) => !row.value);

  return (
    <Page>
      <PageHeader
        title="Platform"
        description="Every business on 7en, what their agents are doing, and what it earns."
      />

      {error && (
        <Callout tone="danger" className="mb-6" title="Couldn’t load the dashboard">
          {error instanceof Error ? error.message : 'Something went wrong fetching the platform figures.'}
        </Callout>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat icon={Building2} label="Businesses" value={count(stats?.total_businesses ?? 0)} href="/v2/businesses" loading={isLoading} />
        <Stat icon={Bot} label="Active agents" value={count(stats?.active_agents ?? 0)} loading={isLoading} />
        <Stat icon={MessageSquare} label="Conversations" value={count(stats?.total_conversations ?? 0)} loading={isLoading} />
        <Stat icon={CreditCard} label="Monthly revenue" value={money(stats?.monthly_revenue ?? 0, { short: true })} loading={isLoading} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard
          title="Growth"
          description="Businesses signing up, and the people they bring with them."
          loading={isLoading}
          empty={!growth.length || growthEmpty}
          action={<SegmentedControl aria-label="Growth period" size="sm" className="[&_button]:text-ui-sm" options={PERIODS} value={period} onValueChange={setPeriod} />}
        >
          <ResponsiveContainer width={"100%"} height={260}>
            <AreaChart data={growth} margin={{ top: 4, right: 8, left: -14, bottom: 0 }}>
              <defs>
                <linearGradient id="growth-businesses" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--v2-brand))" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="hsl(var(--v2-brand))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="growth-users" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--v2-accent))" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="hsl(var(--v2-accent))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis {...axisProps} width={44} />
              <Tooltip cursor={lineCursor} content={<ChartTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Area
                type="monotone"
                dataKey="businesses"
                name="Businesses"
                stroke="hsl(var(--v2-brand))"
                strokeWidth={2}
                fill="url(#growth-businesses)"
              />
              <Area
                type="monotone"
                dataKey="users"
                name="Users"
                stroke="hsl(var(--v2-accent))"
                strokeWidth={2}
                fill="url(#growth-users)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Revenue"
          description="What the platform earns, by where it comes from."
          loading={isLoading}
          empty={!revenueSeries.length || revenueEmpty}
          action={
            <Select
              aria-label="Revenue source"
              size="sm"
              className="w-44"
              options={REVENUE_METRICS}
              value={revenueMetric}
              onValueChange={(value) => setRevenueMetric(value as RevenueMetric)}
            />
          }
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={revenueSeries} margin={{ top: 4, right: 8, left: -4, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis {...axisProps} width={44} tickFormatter={(value: number) => money(value, { short: true, decimals: 0 })} />
              <Tooltip
                cursor={tooltipCursor}
                formatter={(value) => money(value)}
                contentStyle={{
                  backgroundColor: 'hsl(var(--v2-surface))',
                  borderColor: 'hsl(var(--v2-border))',
                  borderRadius: 'var(--ui-radius-sm, 8px)',
                  fontSize: 12,
                }}
                labelStyle={{ color: 'hsl(var(--v2-fg))' }}
                itemStyle={{ color: 'hsl(var(--v2-fg))' }}
              />
              <Bar dataKey="value" name="Revenue" fill="hsl(var(--v2-brand))" radius={[6, 6, 0, 0]} maxBarSize={36} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Conversations"
          description="How much the agents are actually being used, week by week."
          loading={isLoading}
          empty={!conversations.length}
        >
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={conversations} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="week" {...axisProps} />
              <YAxis {...axisProps} width={44} />
              <Tooltip cursor={lineCursor} content={<ChartTooltip />} />
              <Line
                type="monotone"
                dataKey="conversations"
                name="Conversations"
                stroke="hsl(var(--v2-brand))"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Teams"
          description="Members inside those businesses: still here, gone quiet, or just arrived."
          loading={isLoading}
          empty={!teams.length}
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={teams} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="month" {...axisProps} />
              <YAxis {...axisProps} width={44} />
              <Tooltip cursor={tooltipCursor} content={<ChartTooltip total />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar stackId="team" dataKey="active" name="Active" fill="hsl(var(--v2-brand))" maxBarSize={36} />
              <Bar stackId="team" dataKey="new" name="New" fill="hsl(var(--v2-accent))" maxBarSize={36} />
              <Bar
                stackId="team"
                dataKey="inactive"
                name="Inactive"
                fill="hsl(var(--v2-border-strong))"
                radius={[6, 6, 0, 0]}
                maxBarSize={36}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </Page>
  );
}
